import searchTerms, { termsRegex } from "./searchTerms";

/**
 * The part of a message worth showing on a collapsed card, given the search.
 *
 * THE PROBLEM
 *
 * A card shows the first three lines of a message. Dealers post price lists of
 * forty, so searching "5726/1A-014" routinely returns cards whose visible text
 * is the first three unrelated references and whose match is nineteen lines
 * down. The result looks wrong — the row came back from a search whose term is
 * nowhere on it — and the only way to confirm the card is relevant is to open
 * every one of them. WhatsApp's own search solves this by showing the matched
 * fragment instead of the opening one.
 *
 * WHAT IT RETURNS
 *
 * The matched line plus one line either side, with "…" marking that the
 * message continues. Null when there is no query, no match, or nothing to
 * trim — null means "show the message as you always did", so the no-search
 * case is untouched.
 *
 * TWO SHAPES OF MESSAGE, ONE RULE EACH
 *
 * Price lists carry their meaning in line breaks, so the window is measured in
 * LINES. But plenty of messages are a single unbroken paragraph with the match
 * 300 characters in, where a line window is the whole message and helps
 * nobody. So a window that is still too long is cropped again by CHARACTER
 * around the match, snapping to spaces so a reference is never cut in half.
 *
 * WHAT IT DOES NOT DO
 *
 * It does not try to reproduce what the server matched. Indexed search is
 * tokenised and can legitimately return a card that contains no literal
 * occurrence of the query; in that case this returns null and the card shows
 * its opening lines, which is honest. Inventing a snippet would misrepresent
 * why the row came back.
 */

/**
 * Roughly the three clamped lines the card has room for. Going over does not
 * break the layout — the CSS clamp still holds every card to the same height —
 * it just means the tail is cut by the browser rather than chosen here.
 */
const MAX_CHARS = 200;

/** Context before the match when cropping a long line, in characters. */
const LEAD_CHARS = 40;

export default function matchSnippet(text, query) {
  const value = typeof text === "string" ? text : "";
  if (value === "") return null;

  const probe = termsRegex(searchTerms(query), "i");
  if (!probe) return null;

  const lines = value.split("\n");
  const hit = lines.findIndex((line) => probe.test(line));
  if (hit === -1) return null;

  const first = Math.max(0, hit - 1);
  const last = Math.min(lines.length - 1, hit + 1);

  let body = lines.slice(first, last + 1).join("\n");
  let before = first > 0;
  let after = last < lines.length - 1;

  if (body.length > MAX_CHARS) {
    const found = body.match(probe);
    const at = found ? found.index : 0;

    let from = Math.max(0, at - LEAD_CHARS);
    if (from > 0) {
      const space = body.lastIndexOf(" ", from);
      if (space !== -1) from = space + 1;
    }

    let to = Math.min(body.length, from + MAX_CHARS);
    if (to < body.length) {
      const space = body.indexOf(" ", to);
      if (space !== -1) to = space;
    }

    before = before || from > 0;
    after = after || to < body.length;
    body = body.slice(from, to);
  }

  // Nothing was trimmed, so the card is already showing all there is.
  if (!before && !after) return null;

  return (before ? "… " : "") + body + (after ? " …" : "");
}
